/**
 * Content-surface route descriptors — a data-only fragment spread into
 * `ROUTES` (see `./routes.ts`), extracted so the registry table stays under
 * the 400-line `file-size-400` ceiling (FR-045) — the same pattern as
 * `./adminRoutes.ts`. Covers the generic `/content/:slug` article detail, the
 * bespoke proto-aligned pages, the slug-pinned re-export routes, and the 029
 * CMS-driven listings. Push new content descriptors here, not into
 * `routes.ts`.
 */

import { ArticleDetailPage } from '../content/routes/ArticleDetailPage.js';
import { EduhousenVerkkokoulutusRoute } from '../content/routes/EduhousenVerkkokoulutusRoute.js';
import { JasenedutRoute } from '../content/routes/JasenedutRoute.js';
import { LakikaveriInfoRoute } from '../content/routes/LakikaveriInfoRoute.js';
import { LakiruutuRoute } from '../content/routes/LakiruutuRoute.js';
import { OikeusturvavakuutusRoute } from '../content/routes/OikeusturvavakuutusRoute.js';
import { PuhettapalkastaRoute } from '../content/routes/PuhettapalkastaRoute.js';
import { TyosuhteenPaattaminenRoute } from '../content/routes/TyosuhteenPaattaminenRoute.js';
import { VapaaAjanTapaturmavakuutusRoute } from '../content/routes/VapaaAjanTapaturmavakuutusRoute.js';

import {
  EDUHOUSEN_VERKKOKOULUTUS_CONTENT_PATH,
  JASENEDUT_CONTENT_PATH,
  LAKIKAVERI_INFO_CONTENT_PATH,
  LAKIRUUTU_CONTENT_PATH,
  OIKEUSTURVAVAKUUTUS_CONTENT_PATH,
  PUHETTA_PALKASTA_CONTENT_PATH,
  TYOSUHTEEN_PAATTAMINEN_CONTENT_PATH,
  VAPAA_AJAN_TAPATURMAVAKUUTUS_CONTENT_PATH,
} from './contentPagePath.js';
import type { RouteDescriptor } from './routes.js';

export const CONTENT_ROUTES: readonly RouteDescriptor[] = [
  // ----- Generic content routes (the content-and-knowledge feature Phase A, PR B) -----
  //
  // ONE generic surface: the article DETAIL at `/content/:slug`.
  //
  // Home page targets resolve here unless their slug has one of the registered
  // short paths below. Admin preview, typed, and shared URLs may also reach
  // this detail surface. Since the article-LIST page is deleted, it has no
  // separate member-side producer.
  //
  // Two LIST surfaces used to sit above it, `/content` and
  // `/content/category/:slug`. They are DELETED (owner ruling), not redirected
  // and not replaced: they had no counterpart in the prototype, nothing in the
  // app ever linked to them, and they were the only reader of an article's
  // ordinal -- which is why the article ordering axis goes dead in the same
  // slice. Their addresses now behave exactly like every other address this
  // app does not serve -- which since the catch-all landed means they fall to
  // the member not-found surface, by the same route every unknown address
  // takes. Deliberately not special-cased for these two strings: whatever the
  // app answers at an address it does not serve, it answers here too.
  //
  // All Phase A content surfaces are `protected: true, role: 'member'`
  // because the backing `ContentReaderPort` procedures are gated by
  // the API's `sessionProcedure` (the content-and-knowledge feature Phase A backend). Anonymous
  // traffic would surface a tRPC UNAUTHORIZED before reaching the
  // React Query adapter; gating at the router gives a faster,
  // friendlier bounce through `RouteGuard` to `/login`. The same
  // rationale applies to every bespoke, re-export, and stub entry
  // below.
  {
    path: '/content/:slug',
    kind: 'render',
    component: ArticleDetailPage,
    protected: true,
    role: 'member',
    portBoundary: 'content',
  },
  // ----- Bespoke proto-aligned content routes (Phase A PR C.1 + C.2) -----
  //
  // The five proto-aligned URL shapes from `phase-a-content-inventory.md`
  // §"Verified inventory" that lift article prose into a curated route
  // rather than the generic `/content/:slug` template. Each route
  // mounts the bespoke component that owns its widget chrome: the
  // Lakikaveri info screen with its numbered collapsible steps and
  // ContactModal trigger, the Eduhouse online-training page with its
  // catalog widget, the off-duty accident insurance page with its
  // policy-number row, the legal-aid insurance page with its policy
  // chrome, without a Premium destination.
  {
    path: LAKIKAVERI_INFO_CONTENT_PATH,
    kind: 'render',
    component: LakikaveriInfoRoute,
    protected: true,
    role: 'member',
    portBoundary: 'content',
  },
  {
    path: EDUHOUSEN_VERKKOKOULUTUS_CONTENT_PATH,
    kind: 'render',
    component: EduhousenVerkkokoulutusRoute,
    protected: true,
    role: 'member',
    portBoundary: 'content',
  },
  {
    path: OIKEUSTURVAVAKUUTUS_CONTENT_PATH,
    kind: 'render',
    component: OikeusturvavakuutusRoute,
    protected: true,
    role: 'member',
    portBoundary: 'content',
  },
  {
    path: VAPAA_AJAN_TAPATURMAVAKUUTUS_CONTENT_PATH,
    kind: 'render',
    component: VapaaAjanTapaturmavakuutusRoute,
    protected: true,
    role: 'member',
    portBoundary: 'content',
  },

  // ----- Slug-pinned re-export routes (Phase A PR C.1 / TA21) -----
  //
  // Two short URL shapes whose article content lives in the generic
  // content store. The route components are thin re-exports that pin a
  // slug and reuse `ArticleDetailPage` chrome, so the URL stays short
  // without duplicating detail-page composition.
  //
  // This comment used to say the prototype links these URLs directly. It
  // does not, and never did — the addresses are ours. A third shape,
  // `/tama-saat-jasenena`, was registered here on that mistaken basis and
  // is removed along with its seeded article: the prototype's admin
  // manages no such content, so neither do we.
  {
    path: TYOSUHTEEN_PAATTAMINEN_CONTENT_PATH,
    kind: 'render',
    component: TyosuhteenPaattaminenRoute,
    protected: true,
    role: 'member',
    portBoundary: 'content',
  },
  {
    path: PUHETTA_PALKASTA_CONTENT_PATH,
    kind: 'render',
    component: PuhettapalkastaRoute,
    protected: true,
    role: 'member',
    portBoundary: 'content',
  },

  // ----- CMS benefit surface (029-cms-driven-member-surfaces US3) -----
  //
  // 029 US3 (FR-008) turns `/jasenedut` from the Phase A "Tulossa" stub
  // (`JasenedutComingSoon`, #59 retired) into a real CMS-driven listing:
  // `JasenedutRoute` reads published `benefit` content through `useBenefits`
  // (the `ContentReaderPort`), so it carries `portBoundary: 'content'` — a read
  // failure lands on `PortErrorBoundary` -> `ContentUnavailable`, while empty
  // resolves to the route's own empty state. The US1 Home jasenedut card
  // (linkSlug 'jasenedut') now lands on this real listing.
  {
    path: JASENEDUT_CONTENT_PATH,
    kind: 'render',
    component: JasenedutRoute,
    protected: true,
    role: 'member',
    portBoundary: 'content',
  },

  // ----- CMS video_listing surface (029-cms-driven-member-surfaces US2) -----
  //
  // The prototype's menu wires "Lakiruutu" (under Lakikaveri) as a destination.
  // 029 US2 (FR-007) turns it from the 017 honest-demo stub into a real
  // CMS-driven surface: `LakiruutuRoute` reads published `video_listing` content
  // through `useVideoListings` (the `ContentReaderPort`), so it carries
  // `portBoundary: 'content'` — a read failure lands on `PortErrorBoundary` ->
  // `ContentUnavailable`, while empty resolves to the route's own empty state.
  //
  // The sibling "/webinaaritallenteet" stub (017/US3) was removed in 026/US2:
  // the prototype routes no Webinaaritallenteet destination (spec FR-006), so
  // the menu child and its stub route are both gone.
  {
    path: LAKIRUUTU_CONTENT_PATH,
    kind: 'render',
    component: LakiruutuRoute,
    protected: true,
    role: 'member',
    portBoundary: 'content',
  },
];
